import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeSlash } from "@telus/opus-component-library/images"; // Import icons for password visibility toggle
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

// Import redux actions
import { toggleSpinningLoaderAction } from "../../App/state/actions";

// Import custom hooks
import { useReducers } from "../../shared/hooks/useReducer";

// Import utility functions
import { setLocalStorage } from "../../shared/utils/localStorage";

// Import middleware for alternate login
import middleware from "../../shared/middleware";

// Import analytics functions
import { trackWebAction } from "../../shared/analytics/dataLayer";
import { logDTEvent } from "../../shared/analytics/dynatrace";

// Import constants
import constants from "../../shared/constants";
import storageConstants from "../../shared/constants/storage";
import { LoginError, WebLoginErrorMessage } from "../../shared/constants/loginError";
import { ANALYTICS_EVENT_TYPES, ANALYTICS_ERROR_NAMES, ANALYTICS_ERROR_INFO } from "../../shared/constants/analytics";
import { DT_EVENTS, DT_ATTRIBUTES } from "../../shared/constants/dynatrace";

// Import styled components for the login page
import {
  BaseForm,
  BasePasswordInput,
  BasePasswordWrapper,
  BaseRevealPasswordButton,
  BaseSubmitButton,
  BaseTextInput,
  BaseErrorMessageContainer,
  BaseLoginFormContainer,
  LoginInstructions,
  TelusLogoContainer,
  SwitchLoginContainer,
  SwitchLoginButton,
  KoodoLogoContainer,
  AvsLoginBadge,
} from "./AlternateLoginPage.styles";

// Import images
const telusLogo = `${process.env.PUBLIC_URL}/images/logo-telus.svg`;
const koodoLogo = `${process.env.PUBLIC_URL}/images/logo-koodo.svg`;
const errorIcon = `${process.env.PUBLIC_URL}/images/warning-icon.svg`;
const avsLoginBadge = `${process.env.PUBLIC_URL}/images/avs-login-badge.svg`;

// Destructure for easier access
const { alternateLogin } = middleware;
const { LOGIN_BRANDS, REDUCER_TYPE } = constants;
const { ALTERNATE_LOGIN_AUTH_USER_CONTENT, CACHED_ACCOUNT_BRAND } = storageConstants;

/**
 * AlternateLoginPage component provides a login form for users to authenticate with either TELUS or Koodo credentials,
 * when telus identity is down.
 *
 * Features:
 * - Supports toggling between TELUS and Koodo login modes.
 * - Handles username and password input with validation and error messaging.
 * - Allows toggling password visibility.
 * - Disables submit button during form submission.
 *
 * @component
 * @returns {JSX.Element} The rendered login page component.
 */
const AlternateLoginPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { provider, featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isAlternateLoginEnabled, isDynatraceTrackingEnabled } = featureToggles || {};

  const [showKoodoLogin, setShowKoodoLogin] = useState(true); // state to toggle between Koodo and TELUS login
  const [loginError, setLoginError] = useState(null); // state to store any login error messages
  const [isPasswordVisible, setIsPasswordVisible] = useState(false); // state to toggle password visibility
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false); // state to disable the submit button during form submission

  const form = useRef(null); // reference to the form element
  const usernameInput = useRef(null); // reference to the username input field
  const passwordInput = useRef(null); // reference to the password input field
  const dispatch = useDispatch();

  // Handles the edge case when user bookmarks this page but alternate login is disabled (or) we are in production or beta environment
  useEffect(() => {
    if (provider.ENV === "avs-prod" || provider.ENV === "avs-beta" || !isAlternateLoginEnabled) {
      history.replace("/");
    }
  }, [isAlternateLoginEnabled, history, provider]);

  if (!isAlternateLoginEnabled) return null; // Render nothing if alternate login is not enabled

  /**
   * Handles the login form submission by validating the username and password fields.
   * Sets appropriate login error messages if validation fails and prevents form submission.
   * Disables the submit button and clears errors if validation passes.
   *
   * @function
   * @param {React.SyntheticEvent} event - The form submission event.
   */
  const handleLogin = async (event) => {
    event.preventDefault();
    const username = usernameInput.current?.value.trim();
    const password = passwordInput.current?.value.trim();
    if (!username && !password) {
      setLoginError(LoginError.USERNAME_PASSWORD_REQUIRED);
      return;
    } else if (!username) {
      setLoginError(LoginError.USERNAME_REQUIRED);
      return;
    } else if (!password) {
      setLoginError(LoginError.PASSWORD_REQUIRED);
      return;
    }
    setLoginError(null);
    setIsSubmitButtonDisabled(true);
    dispatch(toggleSpinningLoaderAction(true));
    try {
      // Store the login brand in localStorage
      setLocalStorage(CACHED_ACCOUNT_BRAND, showKoodoLogin ? LOGIN_BRANDS.TELUS : LOGIN_BRANDS.KOODO);
      // Call AVS login API
      const response = await alternateLogin(provider, username, password, isDynatraceTrackingEnabled);
      if (response?.data?.resultCode === "OK") {
        setLocalStorage(ALTERNATE_LOGIN_AUTH_USER_CONTENT, response);
        // Remove any previous error
        setLoginError(null);
        // Redirect to home (app will pick up the response and continue the flow)
        window.location.href = "/";
      } else {
        setLoginError(response?.data?.errorDescription || "login_error_message");
      }
    } catch (error) {
      setLoginError(error?.message || "login_error_message");
      trackWebAction(ANALYTICS_EVENT_TYPES.ERROR, {
        name: ANALYTICS_ERROR_NAMES.USER_ERROR,
        details: ANALYTICS_ERROR_INFO.LOGIN_FAILURE,
        message: error?.message,
        code: error?.code,
      });
      logDTEvent(
        DT_EVENTS.LOGIN_ERROR,
        { [DT_ATTRIBUTES.MESSAGE]: error?.message, [DT_ATTRIBUTES.ERROR_RESPONSE]: JSON.stringify(error) },
        isDynatraceTrackingEnabled
      );
    } finally {
      setIsSubmitButtonDisabled(false);
      dispatch(toggleSpinningLoaderAction(false));
    }
  };

  /**
   * Handles the blur event for input fields on the alternate login page.
   * Clears the specific login error if the corresponding input is no longer empty.
   * Also resets the currently focused input reference.
   *
   * @param {React.FocusEvent<HTMLInputElement>} event - The blur event triggered by the input field.
   */
  const handleInputBlur = (event) => {
    const eventTarget = event.target;
    if (loginError && eventTarget.value.trim().length > 0) {
      if (eventTarget === usernameInput.current && loginError === LoginError.USERNAME_REQUIRED) {
        setLoginError(null);
      } else if (eventTarget === passwordInput.current && loginError === LoginError.PASSWORD_REQUIRED) {
        setLoginError(null);
      }
    }
  };

  return (
    <div data-id="login-form-container">
      {showKoodoLogin ? (
        <TelusLogoContainer data-id="logo">
          <img src={telusLogo} alt="Telus Logo" />
        </TelusLogoContainer>
      ) : (
        <KoodoLogoContainer data-id="logo">
          <img src={koodoLogo} alt="Koodo Logo" />
        </KoodoLogoContainer>
      )}
      <AvsLoginBadge data-id="avs-login-badge" src={avsLoginBadge} alt="AVS Login Badge" />
      <BaseLoginFormContainer>
        <LoginInstructions data-id="login-instructions">
          {showKoodoLogin ? t("enter_login") : t("enter_login_koodo")}
        </LoginInstructions>
        <BaseForm ref={form} name="commonEnrolmentForm" method="post" onSubmit={handleLogin}>
          <BaseTextInput
            ref={usernameInput}
            name="username-text-input"
            id="username-text-input"
            placeholder={t("email")}
            type="email"
            onBlur={handleInputBlur}
          />
          <BasePasswordWrapper>
            <BasePasswordInput
              ref={passwordInput}
              name="password-text-input"
              id="password-text-input"
              placeholder={t("password")}
              $isPasswordVisible={isPasswordVisible}
              onBlur={handleInputBlur}
            />
            <BaseRevealPasswordButton
              onClick={() => {
                setIsPasswordVisible((value) => !value);
              }}
            >
              {isPasswordVisible ? <Eye /> : <EyeSlash />}
            </BaseRevealPasswordButton>
          </BasePasswordWrapper>
          {loginError && (
            <BaseErrorMessageContainer data-id="error">
              <img alt="" className="error-icon" src={errorIcon} />
              {t(WebLoginErrorMessage[loginError]) || loginError}
            </BaseErrorMessageContainer>
          )}
          <BaseSubmitButton data-id="submit-button" id="Submit" disabled={isSubmitButtonDisabled}>
            {t("login")}
          </BaseSubmitButton>
        </BaseForm>
      </BaseLoginFormContainer>
      <SwitchLoginContainer>
        <span>{t("or")}</span>
        <SwitchLoginButton data-id="login-switch-button" onClick={() => setShowKoodoLogin((prev) => !prev)}>
          <img src={showKoodoLogin ? koodoLogo : telusLogo} alt={showKoodoLogin ? "Koodo Logo" : "Telus Logo"} />
          <hr />
          <span>{t("login")}</span>
        </SwitchLoginButton>
      </SwitchLoginContainer>
    </div>
  );
};

export default AlternateLoginPage;
